import type { PropsWithChildren } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { noop } from 'lodash';
import { createContext, useContextSelector } from 'use-context-selector';

type AssetClipboardOperation = 'cut' | 'copy';

type AssetClipboard = {
  operation: AssetClipboardOperation | null;
  sourceFolderId: string | null;
  assetIds: string[];
};

type AssetClipboardContextValue = {
  clipboard: AssetClipboard;
  setClipboard: (clipboard: AssetClipboard) => void;
  getClipboard: () => AssetClipboard | null;
};

const AssetClipboardContext = createContext<AssetClipboardContextValue>({
  clipboard: {
    operation: null,
    sourceFolderId: null,
    assetIds: [],
  },
  setClipboard: noop,
  getClipboard: () => null,
});

export const AssetClipboardContextProvider = ({
  children,
}: PropsWithChildren) => {
  const [clipboard, setClipboard] = useState<AssetClipboard>({
    operation: null,
    sourceFolderId: null,
    assetIds: [],
  });

  const getClipboard = useCallback(() => clipboard, [clipboard]);

  const value = useMemo<AssetClipboardContextValue>(
    () => ({
      clipboard,
      setClipboard,
      getClipboard,
    }),
    [clipboard, getClipboard, setClipboard]
  );

  return (
    <AssetClipboardContext.Provider value={value}>
      {children}
    </AssetClipboardContext.Provider>
  );
};

export const useAssetClipboard = () => {
  return useContextSelector(
    AssetClipboardContext,
    (context) => context.clipboard
  );
};

export const useGetAssetClipboard = () => {
  return useContextSelector(
    AssetClipboardContext,
    (context) => context.getClipboard
  );
};

export const useSetAssetClipboard = () => {
  const setClipboard = useContextSelector(
    AssetClipboardContext,
    (context) => context.setClipboard
  );

  return useCallback(
    (clipboard: AssetClipboard) => setClipboard(clipboard),
    [setClipboard]
  );
};

export const useRemoveAssetsFromClipboard = () => {
  const setClipboard = useSetAssetClipboard();
  const getClipboard = useGetAssetClipboard();

  return useCallback(
    (assetIds: string[]) => {
      const clipboard = getClipboard();

      if (!clipboard) return;

      const filteredAssetIds = clipboard.assetIds.filter(
        (id) => !assetIds.includes(id)
      );

      if (filteredAssetIds.length === 0) {
        setClipboard({
          operation: null,
          sourceFolderId: null,
          assetIds: [],
        });
      } else {
        setClipboard({ ...clipboard, assetIds: filteredAssetIds });
      }
    },
    [setClipboard, getClipboard]
  );
};

export const useClearAssetClipboard = () => {
  const setClipboard = useContextSelector(
    AssetClipboardContext,
    (context) => context.setClipboard
  );

  return useCallback(() => {
    setClipboard({
      operation: null,
      sourceFolderId: null,
      assetIds: [],
    });
  }, [setClipboard]);
};

export const useAssetsInClipboard = () => {
  return useContextSelector(
    AssetClipboardContext,
    (context) => context.clipboard.assetIds
  );
};

export const useAssetClipboardOperation = () => {
  return useContextSelector(
    AssetClipboardContext,
    (context) => context.clipboard.operation
  );
};

export const useIsAssetInClipboard = (assetId: string) => {
  return useContextSelector(AssetClipboardContext, (context) =>
    context.clipboard.assetIds.includes(assetId)
  );
};

export const useIsAssetInCopyOperation = (assetId: string) => {
  return useContextSelector(
    AssetClipboardContext,
    (context) =>
      context.clipboard.operation === 'copy' &&
      context.clipboard.assetIds.includes(assetId)
  );
};

export const useIsAssetInCutOperation = (assetId: string) => {
  return useContextSelector(
    AssetClipboardContext,
    (context) =>
      context.clipboard.operation === 'cut' &&
      context.clipboard.assetIds.includes(assetId)
  );
};
